/**
 * WeightSlider - Filter priority weight control
 * 
 * Allows users to set priority weights (0-100%) for search filters.
 * Higher weights mean that filter criteria is more important in ranking results.
 */

import React, { useState, useCallback } from 'react';

export default function WeightSlider({ 
  label,
  value = 50, 
  onChange,
  min = 0,
  max = 100,
  step = 10,
  disabled = false,
  showPercentage = true,
  size = 'default', // 'compact' | 'default'
  color = '#5a20ff'
}) {
  const [isDragging, setIsDragging] = useState(false);
  
  const handleChange = useCallback((e) => {
    const newValue = parseInt(e.target.value, 10);
    onChange?.(newValue);
  }, [onChange]);

  // Calculate fill percentage
  const fillPercentage = ((value - min) / (max - min)) * 100;

  // Dynamic colors based on weight
  const getWeightColor = () => {
    if (value >= 80) return '#52dcb3'; // High priority - teal
    if (value >= 50) return color; // Medium-high - purple
    if (value >= 20) return '#94a3b8'; // Medium-low - gray
    return '#e2e8f0'; // Low - light gray
  };

  const activeColor = getWeightColor();

  if (size === 'compact') {
    return (
      <div className="flex items-center gap-2">
        <style>{`
          .eq-slider-compact::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: white;
            box-shadow: 0 1px 4px rgba(0,0,0,0.15), 0 0 0 2px ${activeColor};
            cursor: pointer;
            transition: transform 150ms ease-out, box-shadow 150ms ease-out;
          }
          .eq-slider-compact::-webkit-slider-thumb:hover {
            transform: scale(1.15);
            box-shadow: 0 2px 8px rgba(0,0,0,0.2), 0 0 0 2px ${activeColor};
          }
          .eq-slider-compact::-moz-range-thumb {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: white;
            box-shadow: 0 1px 4px rgba(0,0,0,0.15), 0 0 0 2px ${activeColor};
            cursor: pointer;
            border: none;
          }
        `}</style>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          className="eq-slider-compact w-16 h-1.5 rounded-full appearance-none cursor-pointer disabled:cursor-not-allowed transition-all duration-200"
          style={{
            background: `linear-gradient(to right, ${activeColor} ${fillPercentage}%, #e2e8f0 ${fillPercentage}%)`,
          }}
        />
        {showPercentage && (
          <span 
            className="text-xs min-w-[28px] text-right tabular-nums font-medium transition-colors duration-200"
            style={{ color: value >= 50 ? activeColor : 'rgba(0,0,0,0.4)' }}
          >
            {value}%
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-1.5 ${disabled ? 'opacity-50' : ''}`}>
      {/* Label Row */}
      {label && (
        <div className="flex items-center justify-between">
          <span 
            className="text-sm font-medium text-black/70"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {label}
          </span>
          {showPercentage && (
            <span 
              className="text-sm font-semibold tabular-nums"
              style={{ color: activeColor }}
            >
              {value}%
            </span>
          )}
        </div>
      )}
      
      {/* Slider Track */}
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleChange}
          onMouseDown={() => setIsDragging(true)}
          onMouseUp={() => setIsDragging(false)}
          onTouchStart={() => setIsDragging(true)}
          onTouchEnd={() => setIsDragging(false)}
          disabled={disabled}
          className="w-full h-2 rounded-full appearance-none cursor-pointer disabled:cursor-not-allowed focus:outline-none"
          style={{
            background: `linear-gradient(to right, ${activeColor} ${fillPercentage}%, #e2e8f0 ${fillPercentage}%)`,
          }}
        />
        
        {/* Visual track marks */}
        <div className="absolute inset-0 flex justify-between items-center px-1 pointer-events-none">
          {[0, 25, 50, 75, 100].map((tick) => (
            <div 
              key={tick}
              className={`w-1 h-1 rounded-full transition-colors ${
                value >= tick ? 'bg-white/50' : 'bg-black/10'
              }`}
            />
          ))}
        </div>
      </div>
      
      {/* Weight Labels */}
      <div className="flex justify-between text-[10px] text-black/40">
        <span>Low</span>
        <span>High</span>
      </div>
    </div>
  );
}

// Export preset configurations
export const WEIGHT_PRESETS = {
  low: 20,
  medium: 50,
  high: 80,
  max: 100,
};

// Utility to calculate weighted match score
export function calculateWeightedScore(lead, filters, weights) {
  if (!filters.length || !Object.keys(weights).length) return 100;
  
  let totalWeight = 0;
  let matchedWeight = 0;
  
  filters.forEach(filter => {
    const weight = weights[filter.field] || 50;
    totalWeight += weight;
    
    // Check if lead matches this filter
    const leadValue = lead[filter.field]?.toLowerCase?.() || '';
    const filterValue = filter.value?.toLowerCase?.() || '';
    
    if (leadValue.includes(filterValue)) {
      matchedWeight += weight;
    }
  });
  
  return totalWeight > 0 ? Math.round((matchedWeight / totalWeight) * 100) : 100;
}



